import { useCallback, useEffect, useState } from 'react'
import { FILE_LIMIT_SIZE_10MB } from '@/constants'
import useDebounceFn from '@/hooks/useDebounceFn'
import {
  useGetSearchAttachment,
  useRemoveImmediateAttachment,
  useSendImmediateAttachment,
} from '@/modules/Users/queries/PatientQueries/Patient.queries'
import { useModalStore } from '@/store/useModal'
import { toast } from '@conexasaude/conexa-hero'

const useViewOrAttachFilesModal = ({ attendanceSelectedAppointment }) => {
  const { modalOpen, closeModal } = useModalStore()
  const attendanceId = attendanceSelectedAppointment

  const [file, setFile] = useState<File>(new File([], String()))

  const { attachmentList, attachmentListMutate, attachmentListIsLoading } =
    useGetSearchAttachment()
  const { sendAttachmentMutation } = useSendImmediateAttachment()
  const { removeAttachmentMutation } = useRemoveImmediateAttachment()

  const handleFileSize = useCallback(() => {
    if (file?.size > FILE_LIMIT_SIZE_10MB) {
      setFile(new File([], String()))
      toast({
        message: 'Arquivo muito grande, o tamanho máximo é de 10MB',
        variant: 'error',
      })
    }
  }, [file])

  const getAttachments = useCallback(() => {
    attachmentListMutate(attendanceId)
  }, [attachmentListMutate, attendanceId])

  const debounceGetAttachments = useDebounceFn(getAttachments, 2000)

  const handleUploadAttachment = useCallback(
    (archive: File[]) => {
      setFile(archive[0])
      const formData = new FormData()
      formData.append('arquivo', archive[0])

      const payload = {
        file: formData,
        id: String(attendanceId),
      }
      sendAttachmentMutation(payload)
      debounceGetAttachments()
    },
    [attendanceId, debounceGetAttachments, sendAttachmentMutation],
  )

  const handleRemoveFile = useCallback(
    (attachmentId: string) => {
      const data = {
        appointmentId: String(attendanceId),
        attachmentId: attachmentId,
      }

      removeAttachmentMutation(data)
      debounceGetAttachments()
    },
    [attendanceId, debounceGetAttachments, removeAttachmentMutation],
  )

  useEffect(() => {
    handleFileSize()
  }, [handleFileSize])

  useEffect(() => {
    getAttachments()
  }, [getAttachments])

  return {
    modalOpen,
    closeModal,
    attachmentList,
    sendAttachmentMutation,
    handleRemoveFile,
    handleUploadAttachment,
    attachmentListIsLoading,
  }
}

export default useViewOrAttachFilesModal
